import './card.scss';
import * as numberToWords from 'number-to-words';
import { memo } from 'react';
import {
  Box,
  TaggableChip,
  SurveyTemplateParser,
  IconLibrary,
  FlexContainer,
} from '@act/storybook';
import InfoCard from 'toolkit/info-card/info-card';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import {
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { needsSelectors } from 'redux/slices/needs.slice';
import { selectNeedsMetadata } from 'redux/slices/metaSlice';
import { surveysSelectors } from 'redux/slices/surveys.slice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { ProgressContainerWithSpinner } from 'react-components/workflows/workflow-dialog-shared.constants';
import useAssignedSurveyDialog from 'react-components/dialog-forms/hooks/useAssignedSurveyDialog';
import useLearnMoreDialog from 'react-components/dialog-forms/hooks/useLearnMoreDialog';
import { DateTime } from 'luxon';

import { HtmlDisplayerWithHyperlinks } from 'react-components/components/HtmlDisplayerWithHyperlinks';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { NoteAddOutlined } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { SectionLinkButton } from '../../../../stories/interaction/Buttons/Section Button/SectionLinkButton';
import { useAppSelector } from 'redux/store-hooks';

interface WorkflowCardProps {
  assignedSurvey?: SurveyV2.AssignedSurveyDto;
  assignedSurveyId?: number;
  completedAt?: number;
  shouldHighlight: boolean;
}

const SurveyCardComponent = ({
  assignedSurvey: inputAssignedSurvey,
  assignedSurveyId,
  completedAt,
  shouldHighlight,
}: WorkflowCardProps) => {
  const statusChips: React.ReactElement[] = [];
  const openLearnMoreDialog = useLearnMoreDialog();
  const openAssignedSurveyDialog = useAssignedSurveyDialog();

  const selectedMember: UserDto = useAppSelector(selectSelectedMember);

  const assignedSurvey =
    inputAssignedSurvey ||
    surveysSelectors.useSelectAssignedSurveyById(assignedSurveyId);

  const completingGuideUser = userAppSelectors.useSelectUserById(
    assignedSurvey?.completedByUserId
  );

  if (!assignedSurvey) return <ProgressContainerWithSpinner />;

  const currentPhase = assignedSurvey.phases
    .filter(phase => phase.checkpoint && phase.completedAt)
    .sort((a, b) => b.completedAt - a.completedAt)[0];

  const completionCheckpoint = currentPhase?.checkpoint;

  const surveyIsComplete = completionCheckpoint?.isCompletionCheckpoint;
  if (surveyIsComplete) {
    statusChips.push(<TaggableChip isTag color="success" label="Complete" />);
  }

  if (!assignedSurvey) {
    return <ProgressContainerWithSpinner />;
  }

  // Check if the survey is late
  const dueTime = currentPhase?.nextPhaseDueAt || assignedSurvey.dueAt || null;
  const now = DateTime.now();

  const isDue = assignedSurvey.dueAt
    ? DateTime.fromJSDate(new Date(dueTime)) < now
    : false;
  if (isDue && !surveyIsComplete) {
    statusChips.push(<TaggableChip isTag color="error" label="Late" />);
  }

  const primaryOption = (
    <Button
      color="primary"
      variant="text"
      aria-label="Open survey dialog"
      onClick={() =>
        openAssignedSurveyDialog({
          assignedSurveyId: assignedSurvey.id,
          isReviewMode: surveyIsComplete,
        })
      }
    >
      {surveyIsComplete ? 'Edit' : 'Complete now'}
    </Button>
  );
  const secondaryOption = (
    <Button
      color="primary"
      variant="text"
      aria-label="Open Learn more dialog"
      onClick={() =>
        openLearnMoreDialog({
          learnMoreContent:
            assignedSurvey?.survey?.learnMoreContent || undefined,
        })
      }
    >
      Learn more
    </Button>
  );

  const lastPhase = assignedSurvey.phases[assignedSurvey.phases.length - 1];

  const dueAt =
    completedAt || currentPhase?.nextPhaseDueAt || assignedSurvey.dueAt;

  let newNeedsCreatedText;
  if (assignedSurvey?.needIds?.length > 0) {
    newNeedsCreatedText = (
      <FlexContainer direction="column" gap={1}>
        <Typography variant="bodyMedium">
          <span className="sentence-case">
            {`${numberToWords.toWords(assignedSurvey.needIds.length)} `}
          </span>
          new need
          {assignedSurvey.needIds.length > 1 ? 's' : ''} created:
        </Typography>
        <FlexContainer direction="row" gap={2} flexShrink={0} padding="8px 0">
          {assignedSurvey.needIds.map(needId => (
            <NeedName key={needId} needId={needId} />
          ))}
        </FlexContainer>
      </FlexContainer>
    );
  }

  const hasCustomSummary = surveyIsComplete && lastPhase?.summary;
  const summaryBody = hasCustomSummary ? (
    <>
      <p>{lastPhase.summary}</p>
      {newNeedsCreatedText}
    </>
  ) : (
    <div>
      <SurveyTemplateParser
        template={
          completionCheckpoint?.summary || assignedSurvey.survey.summary
        }
        member={selectedMember}
        assignedSurvey={assignedSurvey}
      />
    </div>
  );
  return (
    <InfoCard
      variant="task"
      statusChips={statusChips}
      timeAsDue={!completedAt}
      includeTime={false}
      startTime={dueAt ? DateTime.fromJSDate(new Date(dueAt)).toMillis() : null}
      subject={assignedSurvey.survey.displayName || assignedSurvey.survey.name}
      primaryOption={primaryOption}
      secondaryOption={secondaryOption}
      expandedContent={
        surveyIsComplete && assignedSurvey.notes ? (
          <>{HtmlDisplayerWithHyperlinks({ note: assignedSurvey.notes })}</>
        ) : null
      }
      shouldHighlight={shouldHighlight}
    >
      <>
        {surveyIsComplete && (
          <>
            <h4>
              {completingGuideUser
                ? `${completingGuideUser?.firstName} ${completingGuideUser?.lastName} `
                : 'Unknown Guide '}
              completed the {assignedSurvey.survey.displayName} survey
            </h4>
            <Box variant="shadow">
              <>
                <FlexContainer direction="row" alignItems="center" gap={1}>
                  <Typography variant="titleMedium">
                    {assignedSurvey.survey.displayName} survey summary
                  </Typography>
                  {hasCustomSummary &&
                    assignedSurvey.survey.llmLearnMoreContent && (
                      <a
                        href="#"
                        role="contentinfo"
                        className="icon-link"
                        onClick={() => {
                          void openLearnMoreDialog({
                            learnMoreContent:
                              assignedSurvey.survey.llmLearnMoreContent,
                          });
                        }}
                      >
                        <IconLibrary iconName="Sparkle" size={24} />
                      </a>
                    )}
                  {hasCustomSummary &&
                    !assignedSurvey.survey.llmLearnMoreContent && (
                      <IconLibrary iconName="Sparkle" size={24} />
                    )}
                </FlexContainer>
                {summaryBody}
              </>
            </Box>
          </>
        )}
        {!surveyIsComplete && <>{summaryBody}</>}
      </>
    </InfoCard>
  );
};

// Memoize to prevent re-renders when props haven't changed
const SurveyCard = memo(SurveyCardComponent);

const NeedName = ({ needId }: { needId: number }) => {
  const { toMemberPage } = useAppNavigation();

  const need = needsSelectors.useSelectNeedById(needId);
  const issues = useAppSelector(selectNeedsMetadata)?.data?.issues;
  const member = membersAppSelectors.useSelectMemberById(need?.memberId);

  const onClick = () => {
    toMemberPage(
      member?.assignedGuideId,
      member?.id,
      null,
      need?.id.toString(),
      true
    );
  };

  const children = !!need && issues?.find(i => i.id === need.issueId)?.label;

  return (
    <SectionLinkButton
      onClick={onClick}
      startIcon={<NoteAddOutlined />}
      children={children}
    />
  );
};

export default SurveyCard;
